import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Update } from '../types';
import updatesService from '../services/updatesService';
import { categoryLabelFor, categoryStyleFor, formatUpdateDate } from '../utils/updatePresentation';

const UpdateBody: React.FC<{ update: Update }> = ({ update }) => (
    <div className="mt-8 space-y-5">
        {update.body.map((block, index) => {
            if (block.type === 'heading') {
                return (
                    <h2 key={index} className="font-serif text-2xl" style={{ color: 'var(--text-strong)' }}>
                        {block.text}
                    </h2>
                );
            }
            if (block.type === 'list') {
                return (
                    <ul key={index} className="space-y-2">
                        {(block.items ?? []).map((item, i) => (
                            <li key={i} className="flex gap-3">
                                <svg className="mt-1 h-4 w-4 flex-none text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-base leading-relaxed" style={{ color: 'var(--text)' }}>{item}</span>
                            </li>
                        ))}
                    </ul>
                );
            }
            return (
                <p key={index} className="text-base leading-relaxed" style={{ color: 'var(--text)' }}>
                    {block.text}
                </p>
            );
        })}
    </div>
);

const UpdateDetailPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { updateSlug } = useParams<{ updateSlug: string }>();
    const navigate = useNavigate();
    const [update, setUpdate] = useState<Update | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!updateSlug) return;
        let active = true;
        setIsLoading(true);
        updatesService
            .getUpdateBySlug(updateSlug)
            .then(data => {
                if (active) setUpdate(data);
            })
            .finally(() => {
                if (active) setIsLoading(false);
            });
        return () => {
            active = false;
        };
    }, [updateSlug, i18n.language]);

    return (
        <div className="min-h-full pb-20">
            <div className="mx-auto max-w-2xl px-5 py-9 sm:px-8 lg:px-10">
                <button
                    onClick={() => navigate('/updates')}
                    className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-primary-700"
                    style={{ color: 'var(--text-muted)' }}
                >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                    {t('updateDetail.allUpdates')}
                </button>

                {isLoading ? (
                    <div className="flex items-center justify-center py-24">
                        <svg className="h-7 w-7 animate-spin text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                ) : !update ? (
                    <div className="animate-fade-in rounded-2xl border border-primary-100 bg-container p-12 text-center shadow-soft">
                        <h1 className="font-serif text-2xl" style={{ color: 'var(--text-strong)' }}>{t('updateDetail.notFound.title')}</h1>
                        <p className="mt-2" style={{ color: 'var(--text-muted)' }}>
                            {t('updateDetail.notFound.body')}
                        </p>
                        <button
                            onClick={() => navigate('/updates')}
                            className="mt-5 inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-800"
                        >
                            {t('updateDetail.notFound.backToUpdates')}
                        </button>
                    </div>
                ) : (
                    <article className="animate-fade-in">
                        <div className="flex flex-wrap items-center gap-2.5">
                            <span
                                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                                style={{ background: categoryStyleFor(update.category).bg, color: categoryStyleFor(update.category).color }}
                            >
                                {categoryLabelFor(update)}
                            </span>
                            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                                {formatUpdateDate(update.publishedAt)}
                            </span>
                            {update.version && (
                                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>· v{update.version}</span>
                            )}
                        </div>

                        <h1 className="mt-3 font-serif text-4xl leading-tight" style={{ color: 'var(--text-strong)' }}>
                            {update.title}
                        </h1>
                        <p className="mt-3 text-lg leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                            {update.summary}
                        </p>

                        <UpdateBody update={update} />
                    </article>
                )}
            </div>
        </div>
    );
};

export default UpdateDetailPage;
